import React, { useEffect, useState, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { AutoComplete, Input, Modal, Form } from 'antd'
import { useApolloClient } from '@apollo/client'
import gql from 'graphql-tag'
import { LoadingOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'

import * as Utils from '../../components/Utils'

// remove __typename to avoid issues when sending the location to the server
const cleanLocation = (location) => {
	if (!location) return null

	const cleanLocation = {
		...location,
		__typename: undefined,
		coordinates:
			location.coordinates ?
				{
					...location.coordinates,
					__typename: undefined
				}
			:	undefined
	}
	return cleanLocation
}

/**
 * A modal that contains a LocationPickerInput, and handle the onOk and onCancel logic.
 * It is used in the DeviceInfo component to update the device location.
 *
 * Props:
 * - visible: whether the modal is visible or not
 * - onCancel: a callback function that will be called when the modal is closed without saving, with no arguments
 * - onChange: a callback function that will be called when the modal is closed with saving, with the new location as argument
 * - value: the current location value, of type ILocation
 * - title: the title of the modal, of type string
 */
export const LocationPickerModal = ({
	onCancel,
	onChange,
	visible,
	value,
	title
}) => {
	const intl = useIntl()
	const [currentAddress, setCurrentAddress] = useState(value?.address || '')
	const [currentLocation, setCurrentLocation] = useState(cleanLocation(value))

	// when the current value changes, ( for example when the selected device changes or a refresh occurs )
	// or when we reopen the modal
	useEffect(() => {
		setCurrentAddress(value?.address || '')
		setCurrentLocation(cleanLocation(value))
	}, [value, visible])

	// when a location suggestion is selected
	const onLocationPickerChange = (location) => {
		setCurrentLocation({
			...(location ? cleanLocation(location) : {}),
			additionalInfos: currentLocation?.additionalInfos // keep the additional infos if they exist
		})
	}

	const onAdditionalInfosChange = (e) => {
		setCurrentLocation({
			...(currentLocation ? cleanLocation(currentLocation) : {}),
			additionalInfos: e.target.value
		})
	}

	const handleOk = () => {
		onChange(currentLocation)
	}

	return (
		<Modal
			width={'40%'}
			visible={visible}
			onCancel={onCancel}
			onOk={handleOk}
			title={title}
			// important to avoid keeping the state of the LocationPickerInput when reopening the modal
			destroyOnClose
		>
			<Form labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
				<Form.Item
					label={
						<FormattedMessage id="location.form.locationPicker.label" />
					}
				>
					<LocationPickerInput
						address={currentAddress || ''}
						onChange={onLocationPickerChange}
					/>
				</Form.Item>
				<Form.Item
					label={
						<FormattedMessage id="location.form.additionalInfos.label" />
					}
				>
					<Input.TextArea
						rows={4}
						placeholder={intl.formatMessage({
							id: 'location.form.additionalInfos.placeholder'
						})}
						value={currentLocation?.additionalInfos || ''}
						onChange={onAdditionalInfosChange}
					/>
				</Form.Item>
			</Form>
		</Modal>
	)
}

/**
 * This component is used to pick a location using the Google Places API.
 * The location are for now related only to devices, but this component
 * is context agnostic and could be used anywhere we need to pick a location.
 *
 * Props:
 * - address: the current address to be displayed in the input, of type string
 * - onChange: a callback function that will be called when the location changes, with the new location as argument
 */
export const LocationPickerInput = ({ address, onChange }) => {
	const client = useApolloClient()
	const intl = useIntl()

	const [displayValue, setDisplayValue] = useState(address ?? '')
	const [options, setOptions] = useState([])
	const [fetching, setFetching] = useState(false)

	useEffect(() => {
		setDisplayValue(address ?? '')
	}, [address])

	// it represents the Google Places API search session
	// it is returned by the first call, and the session end when we select a location
	const [sessionToken, setSessionToken] = useState()

	const fetchOptions = async (query) => {
		if (!query) {
			setOptions([])
			return
		}

		setFetching(true)
		try {
			const response = await client.query({
				variables: { query, sessionToken },
				fetchPolicy: 'no-cache',
				query: gql`
					query getAddressSuggestions(
						$query: String!
						$sessionToken: String
					) {
						getAddressSuggestions(
							query: $query
							sessionToken: $sessionToken
						) {
							sessionToken
							suggestions {
								placeId
								texts
							}
						}
					}
				`
			})

			// update the session token for the next call
			setSessionToken(response.data.getAddressSuggestions.sessionToken)
			const suggestions =
				response.data?.getAddressSuggestions?.suggestions ?? []

			const formattedOptions = suggestions.map((suggestion) => ({
				value: suggestion.texts.join(', '),
				label: suggestion.texts.join(', '),
				key: suggestion.placeId,
				suggestion
			}))

			setOptions(formattedOptions)
		} catch (e) {
			Utils.displayError(e)
		} finally {
			setFetching(false)
		}
	}

	const debouncedFetchOptions = useCallback(
		debounce(fetchOptions, 500),
		[sessionToken] // re-create when sessionToken changes
	)

	const fetchLocationDetails = async (placeId) => {
		try {
			const response = await client.query({
				variables: { placeId, sessionToken },
				query: gql`
					query getLocationFromPlaceId(
						$placeId: String!
						$sessionToken: String
					) {
						getLocationFromPlaceId(
							placeId: $placeId
							sessionToken: $sessionToken
						) {
							address
							streetNumber
							street
							neighborhood
							subLocality
							city
							country
							countryCode
							postalCode
							coordinates {
								latitude
								longitude
							}
							placeId
						}
					}
				`
			})

			const location = response.data?.getLocationFromPlaceId
			if (location) {
				const cleaned = cleanLocation(location)
				setDisplayValue(cleaned.address)
				onChange(cleaned)
			}
		} catch (e) {
			Utils.displayError(e)
		} finally {
			setSessionToken(null) // end the session
		}
	}

	return (
		<AutoComplete
			allowClear={true}
			value={displayValue}
			options={options}
			onSearch={debouncedFetchOptions}
			onChange={(newValue) => {
				// required to keep the display value in sync
				// and to workaround some weird behavior of the AutoComplete component
				setDisplayValue(newValue ?? '')
				if (!newValue) {
					onChange(null) // if the input is cleared, we consider that the location is removed
				}
			}}
			onSelect={(value, option) => {
				fetchLocationDetails(option.suggestion.placeId)
			}}
			style={{ width: '100%' }}
			notFoundContent={fetching ? <LoadingOutlined spin /> : null}
			placeholder={intl.formatMessage({
				id: 'location.form.locationPicker.placeholder'
			})}
		></AutoComplete>
	)
}
